import React from "react";
import { useTranslation } from "react-i18next";
import type {
  TransformedCartTotals,
  TransformedCartFee,
  TransformedCartCoupon,
} from "../../../cart/types/cartApiTypes";

interface CheckoutTotalsProps {
  totals: TransformedCartTotals;
  fees: TransformedCartFee[];
  coupons: TransformedCartCoupon[];
  needsShipping: boolean;
  hasCalculatedShipping: boolean;
  isFreeShipping: boolean;
  currencySymbol: string;
}

const CheckoutTotals: React.FC<CheckoutTotalsProps> = ({
  totals,
  fees,
  coupons,
  needsShipping,
  hasCalculatedShipping,
  isFreeShipping,
  currencySymbol,
}) => {
  const { t } = useTranslation();

  const hasTax = totals.totalTax > 0;

  return (
    <div className="order-summary-totals">
      {/* Subtotal */}
      <div className="totals-row">
        <span className="totals-label">
          {t("cart.totals.subtotalWithTax", "Subtotal (incl. VAT)")}
        </span>
        <span className="totals-value">
          {totals.formattedTotalItems}
          {currencySymbol}
        </span>
      </div>

      {/* Shipping */}
      {needsShipping && (
        <div className="totals-row">
          <span
            className={`totals-label ${
              isFreeShipping ? "free-shipping-label" : ""
            }`}
          >
            {isFreeShipping
              ? t("cart.totals.freeShipping", "Free Shipping")
              : t("cart.totals.otherShipping", "Shipping Cost")}
          </span>
          <span className="totals-value">
            {hasCalculatedShipping
              ? `${totals.formattedTotalShipping}${currencySymbol}`
              : t("cart.shippingCalculating", "Calculating...")}
          </span>
        </div>
      )}

      {/* Optional: Fees */}
      {fees.map((fee) => (
        <div key={fee.id} className="totals-row">
          <span className="totals-label">{fee.name}</span>
          <span className="totals-value">
            {fee.formattedTotal}
            {currencySymbol}
          </span>
        </div>
      ))}

      {/* Optional: Discounts */}
      {coupons.map((coupon) => (
        <div key={coupon.code} className="totals-row discount">
          <span className="totals-label">
            {t("cart.couponApplied", "Coupon: {{code}}", {
              code: coupon.code,
            })}
          </span>
          <span className="totals-value">
            -{totals.formattedTotalDiscount}
            {currencySymbol}
          </span>
        </div>
      ))}

      {/* Display Tax Row --- */}
      {hasTax && (
        <div className="totals-row tax-item">
          <span className="totals-label">
            {t("cart.totals.vat", "VAT (10%)")}
          </span>
          <span className="totals-value">
            {totals.formattedTotalTax}
            {currencySymbol}
          </span>
        </div>
      )}

      {/* Grand Total */}
      <div className="totals-row grand-total">
        <strong className="totals-label">{t("cart.totals.total")}</strong>
        <strong className="totals-value">
          {totals.formattedTotalPrice}
          {currencySymbol}
        </strong>
      </div>
    </div>
  );
};

export default CheckoutTotals;
